import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TableInterface from "@homegame/common/src/interface/table.interface";
import { Controller, useForm } from "react-hook-form";
import { useAuth } from "../core/context/auth.context";
import TableService from "../core/service/table.service";
import { Link } from "react-router-dom";
import HeadingComponent from "../core/components/heading.component";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { useToast } from "../core/hooks/use-toast.hook";
import Button from "@mui/material/Button";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      maxWidth: 500,
      margin: "0 auto",
      textAlign: "center",
      paddingBottom: 120,
      "&:after": {
        backgroundImage: "url(/cards.svg)",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "70%",
        content: '""',
        display: "block",
        position: "absolute",
        width: "100%",
        height: "100%",
        filter: "opacity(0.1)",
        top: 0,
        left: 0,
        zIndex: -1,
      },
    },
    wrapper: {
      background:
        "linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.015))",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(16px)",
      borderRadius: 15,
      padding: "32px 24px",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
      marginTop: 70,
    },
    headingBtns: {
      // @ts-ignore
      float: "left !important",
      "&.Mui-disabled": {
        opacity: 0.3,
      },
    },
  }),
);

export default function TableForm({
  initialValues,
  onSave,
}: {
  initialValues?: TableInterface | undefined;
  onSave: (table: TableInterface) => void;
}) {
  const { showToast } = useToast();
  const classes: any = useStyles({});
  const tableService = new TableService();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { user } = useAuth();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ defaultValues: initialValues });

  const onSubmit = async (userData: any) => {
    try {
      if (!user) return;
      const newData = userData;

      newData.userId = user.id;
      setIsSaving(true);

      const savedData = await tableService.save(newData, initialValues?.id);
      onSave(savedData);
      showToast("Table saved", "success");
    } catch (e: any) {
      let errorMsg = e.message;

      if (e.data && e.data.length) {
        e.data.forEach(({ msg, param }: { msg: string; param: string }) => {
          errorMsg += `. ${param}: ${msg}`;
        });
      }
      showToast(errorMsg, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // @ts-ignore
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        maxWidth={500}
        margin="0 auto"
        className={classes.container}
      >
        <Grid item xs={12} pl={0} ml={0}>
          <HeadingComponent text={initialValues ? "Edit table" : "Add table"} />
          <div className="heading-buttons">
            <IconButton
              className={classes.headingBtns}
              disabled={isSaving}
              component={Link}
              to={`/tables/${initialValues ? initialValues.id + "/view" : ""}`}
              aria-label="back"
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
        </Grid>
        <Grid item xs={12} mt={4} className={classes.wrapper}>
          <Grid container spacing={3}>
            <Grid item sm={12} xs={12}>
              <Typography>Table Name</Typography>
              <FormControl fullWidth style={{ display: "inline-block" }}>
                {/* @ts-ignore*/}
                <Controller
                  render={({ field }) => (
                    <TextField
                      helperText={errors.name ? errors.name.message : ""}
                      error={!!errors.name}
                      fullWidth
                      disabled={isSaving}
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        input: { color: "#fff" },
                        label: { color: "rgba(255,255,255,0.7)" },
                        background: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(8px)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                      {...field}
                    />
                  )}
                  name="name"
                  rules={{
                    required: "Name required!",
                    minLength: {
                      value: 3,
                      message: "Min name length is 3",
                    },
                  }}
                  control={control}
                  defaultValue={initialValues?.name}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                startIcon={<SaveIcon />}
                variant="outlined"
                color="success"
                type="submit"
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
}
